import React from "react";
import styles from "./home.module.scss";
import {
  InformationComponent,
  InformationMedia,
} from "../../components/information/information.component";
import { HackathonCarousel } from "../../components/hackathon-carousel/HackathonCarousel.component";
import { TabKeys } from "../../data/tabs";
import { useMenu } from "../../components/menu/useMenu";
import AboutImage from "@assets/images/about.jpg";
import BeginningImage from "@assets/images/beginning.jpg";
import HackathonImage from "@assets/images/hackathon.jpg";
import StartupsImage from "@assets/images/startups.jpg";
import ThoughtsImage from "@assets/images/thoughts.jpg";
import {
  calendarIcon,
  ethIcon,
  globeIcon,
  lightbulbIcon,
  unlockIcon,
} from "../../utils/svg";

export const Home: React.FC = () => {
  return (
    <div className={styles["home"]}>
      {/* Five sections, five different answers to "where does the art sit",
          all at the source's native 4:3: About and Startups are magazine
          floats (wide / default), The Beginning a masthead banner (header
          riding ON the full image, ring painted over it), Blockchain a caption
          chassis around a full-width framed banner, Thoughts a closing plate
          under the CTA. */}
      <InformationComponent
        icon={lightbulbIcon}
        sizeX={384}
        sizeY={512}
        kicker="THE JOURNEY"
        title="About"
        image={AboutImage}
        imageAlt="Golden fractal light blooming from a glass monitor in a night desert"
        // Full 4:3, uncropped; wide float so the fractal detail reads. The
        // welcome line opens the card full-width above the art — beside a
        // 62% panel it degrades to a few words per line.
        imagePosition="left"
        imageShape="wide"
        lead={<AboutLead />}
        leadAbove>
        <About />
      </InformationComponent>
      <InformationComponent
        icon={calendarIcon}
        sizeX={448}
        sizeY={512}
        kicker="ORIGINS"
        title="The Beginning"
        image={BeginningImage}
        imageAlt="A 1990s computer setup reimagined in glass and gold, floating in a moonlit attic"
        // Full image at native ratio — the masthead rides in the attic's dark
        // top band; no focal window because nothing is cropped.
        variant="banner">
        <Start />
      </InformationComponent>
      <InformationComponent
        icon={ethIcon}
        sizeX={256}
        sizeY={417}
        kicker="THE FRONTIER"
        title="Blockchain">
        <Blockchain />
      </InformationComponent>
      <InformationComponent
        icon={globeIcon}
        sizeX={496}
        sizeY={512}
        kicker="LIFT-OFF"
        title="Startups"
        image={StartupsImage}
        imageAlt="A crystalline vessel lifting off from a chrome launch gantry at night"
        imagePosition="right"
        // Quote opens the card full-width; the panel floats right of the
        // body copy ("Working to improve…"), which wraps beside it.
        lead={<StartupsLead />}
        leadAbove>
        <Startups />
      </InformationComponent>
      <InformationComponent
        icon={unlockIcon}
        sizeX={448}
        sizeY={512}
        kicker="OPEN CHANNEL"
        title="Thoughts"
        image={ThoughtsImage}
        imageAlt="An open chrome gate spilling golden light across a mirrored salt flat"
        // The art is the card: 4:3 backdrop, copy in the starfield sky, the
        // CTA standing on the bridge at the bottom.
        variant="poster">
        <Thoughts />
      </InformationComponent>
    </div>
  );
};

const AboutLead: React.FC = () => {
  return (
    <>
      Welcome to my personal website, where I share my journey as a software
      developer, which began at <strong>the age of 13</strong>.
    </>
  );
};

const About: React.FC = () => {
  return (
    <>
      <p>
        My fascination with computers started with their ability to&nbsp;
        <a
          href="https://en.wikipedia.org/wiki/Demoscene"
          target="_blank"
          rel="noreferrer">
          visualise
        </a>
        &nbsp;math and procedural graphics and grew into a passion for exploring
        the <strong>endless possibilities of technology</strong>.
      </p>
    </>
  );
};

const Start: React.FC = () => {
  return (
    <div className={styles["column"]}>
      <blockquote>
        As a teenager in the &rsquo;90s I programmed games, desktop apps and
        audio-visual software.
      </blockquote>
      <p>
        When the internet became more accessible during my student years, I
        turned to websites and experimental web technology, while also exploring
        music composition, audio production and procedural generation.
      </p>
      <p>
        After several freelance projects and a year at a tech company in
        Denmark, I joined an international logistics firm at its head office in
        the Netherlands. Over <strong>14 years</strong> I grew from developer to
        the <strong>Solution Architect</strong> at a 120-person IT department,
        delivering projects around the globe.
      </p>
    </div>
  );
};

const Blockchain: React.FC = () => {
  return (
    <div className={styles["column"]}>
      <blockquote>
        My interest in blockchain began in its early days, before the advent of
        Ethereum.
      </blockquote>
      <p>
        I mined&nbsp;
        <a href="https://github.com/bitcoin" target="_blank" rel="noreferrer">
          Bitcoin
        </a>
        &nbsp;and experimented with its codebase, recognising the potential of
        this emerging technology. When&nbsp;
        <a href="https://github.com/ethereum" target="_blank" rel="noreferrer">
          Ethereum
        </a>
        &nbsp;launched in 2015, I was excited by the possibilities of&nbsp;
        <strong>smart contracts</strong> and the ecosystems they could form,
        envisioning <strong>a fairer, more decentralised future</strong>, and I
        have been building ever since.
      </p>
      <InformationMedia
        image={HackathonImage}
        alt="Rows of glowing workstations converging on a crystalline hologram in a dark hall"
        position="top"
        caption="Hackathon floor"
      />
      <p>
        A hackathon is first and foremost an experience: a few sleep-deprived
        days in which an idea must become a working demo, in a room humming with
        teams chasing the same deadline. You walk out having learned more in a
        weekend than months of ordinary work could teach, and knowing brilliant
        people you would never otherwise have crossed paths with. These are the
        events where I have&nbsp;
        <strong>competed and mentored</strong> over the years:
      </p>
      <HackathonCarousel />
      <p>
        I have also had the delight of mentoring at the&nbsp;
        <a href="https://www.ethdam.com/" target="_blank" rel="noreferrer">
          EthDAM
        </a>
        &nbsp;hackathon in 2023 and 2024, providing technical support while
        learning so much from all the brilliant minds participating. Through
        these experiences I have been able to expand my skills, take deep dives
        into new technologies and contribute to meaningful projects.
      </p>
    </div>
  );
};

// Pulled out of the section so the staggered overlap can set it on its own
// plate; it reads as the card's opening line either way.
const StartupsLead: React.FC = () => {
  return (
    <>
      My passion for decentralisation led me to leave my day job in 2020 and
      co-found a startup.
    </>
  );
};

const Startups: React.FC = () => {
  return (
    <div className={styles["column"]}>
      <p>
        Working to improve the job, learning and assessment industries, we&nbsp;
        <strong>won Odyssey Momentum</strong>, were selected for the&nbsp;
        <a href="https://www.yesdelft.com/" target="_blank" rel="noreferrer">
          Yes!Delft
        </a>
        &nbsp;incubator, and went on to complete a nine-month European Union
        programme with&nbsp;
        <a href="https://essif-lab.eu/" target="_blank" rel="noreferrer">
          NGI eSSIF-LAB
        </a>
        , collaborating with other projects to drive adoption of&nbsp;
        <a
          href="https://en.wikipedia.org/wiki/Self-sovereign_identity"
          target="_blank"
          rel="noreferrer">
          Self-Sovereign Identity
        </a>
        &nbsp;across the EU.
      </p>
      <p>
        Along the way I have built on both permissioned and public blockchains,
        and it has been rewarding to watch enterprises come to embrace{" "}
        <strong>public chains as neutral ground</strong> for their business
        networks. Since then the startup journey has continued: I have been
        involved in several more ventures, and I am getting new ones off the
        ground today.
      </p>
    </div>
  );
};

const Thoughts: React.FC = () => {
  const { onTabSelected } = useMenu();

  return (
    <div className={styles["column"]}>
      <blockquote>
        There is <strong>a vast realm of possibilities</strong> to explore,
        potential futures to imagine, and a fairer, more decentralised world to
        help build.
      </blockquote>
      <p>
        If any of this resonates,&nbsp;
        <button
          type="button"
          className={styles["link-button"]}
          onClick={(): void => onTabSelected(TabKeys.Contact)}>
          connect with me
        </button>
        . I would love to <strong>exchange ideas</strong>.
      </p>
      <button
        type="button"
        className={styles["contact-button"]}
        onClick={(): void => onTabSelected(TabKeys.Contact)}>
        Contact
      </button>
    </div>
  );
};
